import { useCallback, useEffect, useMemo, useRef, type PropsWithChildren } from 'react';
import {
  createAnalytics, isAnalyticsConfigured, readAnalyticsConfig, type AnalyticsAdapter,
  type AnalyticsEventName, type AnalyticsPayload,
} from '@/analytics';
import { AnalyticsContext, type AnalyticsContextValue } from '@/contexts/AnalyticsContext';
import { useSettings } from '@/hooks/useSettings';

const config = readAnalyticsConfig();

/**
 * Analytics is off until the user turns it on, and the vendor script is not
 * loaded before then. Withdrawing consent shuts the adapter down immediately.
 */
export function AnalyticsProvider({ children }: PropsWithChildren) {
  const { settings, update } = useSettings();
  const hasConsent = settings.analyticsConsent === true;
  const adapterRef = useRef<AnalyticsAdapter | null>(null);

  useEffect(() => {
    adapterRef.current?.shutdown();
    adapterRef.current = createAnalytics(config, hasConsent);
    return () => {
      adapterRef.current?.shutdown();
      adapterRef.current = null;
    };
  }, [hasConsent]);

  const track = useCallback(
    <T extends AnalyticsEventName>(event: T, payload: AnalyticsPayload<T>) => {
      adapterRef.current?.track(event, payload);
    },
    [],
  );

  const setConsent = useCallback(
    (granted: boolean) => {
      void update({ analyticsConsent: granted });
    },
    [update],
  );

  const value = useMemo<AnalyticsContextValue>(
    () => ({ track, isConfigured: isAnalyticsConfigured(config), hasConsent, setConsent }),
    [track, hasConsent, setConsent],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}
